import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getAgenticPromptMetadata } from 'services/agenticPrompts'

/**
 * Fetches the agentic prompt metadata used to populate the workflow/app-domain pickers.
 * The list is small and admin-curated, so it is cached for the session rather than refetched
 * per screen.
 * @returns {{
 *   workflows: Array<{key: string, label: string, description: string}>,
 *   appDomains: Array<{key: string, label: string}>,
 *   workflowOptions: Array<{value: string, label: string}>,
 *   appDomainOptions: Array<{value: string, label: string}>,
 *   getWorkflowLabel: (key: string) => string,
 *   getAppDomainLabel: (key: string) => string,
 *   isLoading: boolean,
 * }} metadata lists, select options, and label lookup helpers
 */
export function usePromptMetadata() {
  const { data, isLoading } = useQuery({
    queryKey: ['agenticPromptMetadata'],
    queryFn: getAgenticPromptMetadata,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  })

  const workflows = useMemo(() => data?.workflows ?? [], [data?.workflows])
  const appDomains = useMemo(() => data?.appDomains ?? [], [data?.appDomains])

  const workflowOptions = useMemo(
    () =>
      workflows.map(wf => ({
        value: wf.key,
        label: wf.label,
      })),
    [workflows],
  )

  const appDomainOptions = useMemo(
    () =>
      appDomains.map(domain => ({
        value: domain.key,
        label: domain.label,
      })),
    [appDomains],
  )

  const workflowMap = useMemo(() => new Map(workflows.map(wf => [wf.key, wf.label])), [workflows])

  const appDomainMap = useMemo(() => new Map(appDomains.map(d => [d.key, d.label])), [appDomains])

  const getWorkflowLabel = useCallback(
    key => (key ? workflowMap.get(key) || key : ''),
    [workflowMap],
  )

  const getAppDomainLabel = useCallback(
    key => (key ? appDomainMap.get(key) || key : ''),
    [appDomainMap],
  )

  return {
    workflows,
    appDomains,
    workflowOptions,
    appDomainOptions,
    getWorkflowLabel,
    getAppDomainLabel,
    isLoading,
  }
}
